import { Component } from '@theme/component';

const SHOW_SCROLL_THRESHOLD = 40;
const HIDE_SCROLL_THRESHOLD = 80;

/**
 * Announcement banner custom element that supports slideshow rotation,
 * session dismissal and scroll-aware visibility.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 * @property {HTMLButtonElement} [closeButton]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;
  #interval = undefined;
  #scrollRafId = null;
  #manuallyDismissed = false;
  #section = null;
  #aside = null;
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section');
    this.#aside = this.closest('.arara-announcement');
    this.#manuallyDismissed = this.#isDismissedForSession();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.refs.closeButton?.addEventListener('click', this.#dismiss);

    if (this.hideOnScroll && !this.#manuallyDismissed) {
      window.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }

    if (this.#aside && this.#section) {
      this.#resizeObserver = new ResizeObserver(this.#updateSectionHeight);
      this.#resizeObserver.observe(this.#aside);
      this.#updateSectionHeight();
    }

    this.#setHidden(this.#manuallyDismissed || (this.hideOnScroll && window.scrollY > HIDE_SCROLL_THRESHOLD));
    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.suspend();
    this.#resizeObserver?.disconnect();
    this.refs.closeButton?.removeEventListener('click', this.#dismiss);
    this.removeEventListener('mouseenter', this.suspend);
    this.removeEventListener('mouseleave', this.resume);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    window.removeEventListener('scroll', this.#handleWindowScroll);

    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
  }

  get hideOnScroll() {
    return this.dataset.hideOnScroll === 'true';
  }

  get storageKey() {
    return this.dataset.sessionKey || 'araraAnnouncementDismissed';
  }

  #isDismissedForSession() {
    try {
      return sessionStorage.getItem(this.storageKey) === 'true';
    } catch (error) {
      return false;
    }
  }

  #dismiss = () => {
    this.#manuallyDismissed = true;

    try {
      sessionStorage.setItem(this.storageKey, 'true');
    } catch (error) {
      // The visual dismissal still works if storage is unavailable.
    }

    window.removeEventListener('scroll', this.#handleWindowScroll);
    this.#setHidden(true);
  };

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null || this.#manuallyDismissed) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollVisibility();
    });
  };

  #updateScrollVisibility() {
    const scrollTop = window.scrollY || document.scrollingElement?.scrollTop || 0;

    if (scrollTop > HIDE_SCROLL_THRESHOLD) {
      this.#setHidden(true);
    } else if (scrollTop < SHOW_SCROLL_THRESHOLD) {
      this.#setHidden(false);
    }
  }

  #setHidden(hidden) {
    if (!this.#section) return;

    this.#section.classList.toggle('arara-announcement-section--hidden', hidden);
    this.#section.dataset.announcementState = hidden ? 'hidden' : 'visible';
    this.#aside?.toggleAttribute('inert', hidden);
    this.#aside?.setAttribute('aria-hidden', `${hidden}`);
  }

  #updateSectionHeight = () => {
    if (!this.#section || !this.#aside) return;

    const height = Math.ceil(this.#aside.getBoundingClientRect().height);
    if (height > 0) {
      this.#section.style.setProperty('--arara-announcement-height', `${height}px`);
    }
  };

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;
    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;
      this.next();
    }, interval);
  }

  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  suspend = () => {
    clearInterval(this.#interval);
    this.#interval = undefined;
  };

  resume = () => {
    if (!this.autoplay || this.paused) return;

    this.suspend();
    this.play();
  };

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);
    return Number.isNaN(value) ? undefined : value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) relativeIndex += (this.refs.slides ?? []).length;

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

